// todo: This file is a mess...
/* eslint-disable @typescript-eslint/no-use-before-define */
import { capitalise } from "@wisr/common";

import { quoteAmplitudeHelper } from "../shared/analytics/analytics.utils";
import { Application, SectionErrors, Status } from "./application";
import {
  PaymentFrequency,
  applicationFormDefaults,
} from "./application-form.constants";
import { Debt, FinancialDebtId, getFieldKeys } from "./owing/owing.component";
import {
  CREDIT_CARD_VALUE,
  MORTGAGE_VALUE,
  PERSONAL_LOAN_VALUE,
} from "./multi-page-application-form/form-pages/your-debts/debt-list/debt-list.constants";
import { IncompleteLoanApplication } from "../types/personal-loan";
import { ABTestProperties } from "../shared/analytics/analytics.consts";

export const LIVING_SITUATION_MORTGAGE = "Buyer";

export const ID_TYPES = {
  DRIVERS_LICENCE: "Driver's License",
  MEDICARE: "Medicare card",
  PASSPORT: "Passport",
} as const;

export const hasAddedDebts = (formData: Application): boolean => {
  return getDebtFields(formData).length > 0;
};

export const getDebtFields = (formData: Application): Debt[] => {
  const debts: Debt[] = [];

  for (let i = 1; i <= 10; i++) {
    const {
      debtTypeKey,
      financeCompanyKey,
      balanceKey,
      repaymentAmountKey,
      repaymentFrequencyKey,
      isJointLoanKey,
      consolidateKey,
      investmentKey,
      cardLimitKey,
      closeCardKey,
    } = getFieldKeys(i as FinancialDebtId);
    debts.push({
      type: formData[debtTypeKey],
      company: formData[financeCompanyKey],
      balance: formData[balanceKey],
      repaymentAmount: formData[repaymentAmountKey],
      frequency: formData[repaymentFrequencyKey],
      isJoint: formData[isJointLoanKey],
      consolidate: formData[consolidateKey],
      investment: formData[investmentKey],
      cardLimit: formData[cardLimitKey],
      closeCard: formData[closeCardKey],
    });
  }

  return debts.filter((debt) => debt.type !== "");
};

function getAnnualAmount(amount: number, frequency: string) {
  const frequencyEnum = frequency as PaymentFrequency;

  let multiplier = 1;

  switch (frequencyEnum) {
    case PaymentFrequency.MONTHLY:
      multiplier = 12;
      break;
    case PaymentFrequency.WEEKLY:
      multiplier = 52;
      break;
    case PaymentFrequency.FORTNIGHTLY:
      multiplier = 26;
      break;

    case PaymentFrequency.ANNUAL:
      multiplier = 1;
      break;
    default:
      multiplier = 1;
  }
  return amount * multiplier;
}

export const checkForFormLevelErrors = (formData: Application) => {
  const formErrors: SectionErrors = {};

  formErrors["incomeError"] = getIncomeErrors(formData);
  formErrors["spendingError"] = getSpendingErrors(formData);
  formErrors["owingError"] = getOwingErrors(formData);

  return formErrors;
};

const getIncomeErrors = (formData: Application): undefined | Status[] => {
  const minimumIncome = 25000;
  const errorMsg = "Your total income must be at least $25,000 per year";

  const primaryIncome = getAnnualAmount(
    +formData.incomeAmount,
    formData.incomeFrequency
  );
  const otherIncome = getAnnualAmount(
    +formData.otherIncomeAmount_1,
    formData.otherIncomeFrequency_1
  );
  const otherIncome2 = getAnnualAmount(
    +formData.otherIncomeAmount_2,
    formData.otherIncomeFrequency_2
  );
  const otherIncome3 = getAnnualAmount(
    +formData.otherIncomeAmount_3,
    formData.otherIncomeFrequency_3
  );

  const totalIncome = primaryIncome + otherIncome + otherIncome2 + otherIncome3;

  if (totalIncome < minimumIncome) {
    return [{ kind: "error", message: errorMsg }];
  }

  return undefined;
};

const getSpendingErrors = (formData: Application): undefined | Status[] => {
  const minimumSpent = 0;
  const errorMsg = "Your total monthly living expenses must be greater than $0";

  const totalExpenses =
    +formData.groceries +
    +formData.clothing +
    +formData.council_rates +
    +formData.car_registration +
    +formData.petrol +
    +formData.car_home_insurance +
    +formData.utilities +
    +formData.communication +
    +formData.transportation +
    +formData.entertainment +
    +formData.other_general_expenses;

  if (totalExpenses <= minimumSpent) {
    return [{ kind: "error", message: errorMsg }];
  }

  return undefined;
};

export const checkForLoanOrCards = (formData: Application): Debt[] => {
  return getDebtFields(formData).filter(
    (debt) =>
      debt.type === PERSONAL_LOAN_VALUE || debt.type === CREDIT_CARD_VALUE
  );
};

const getOwingErrors = (formData: Application): undefined | Status[] => {
  const statuses: Status[] = [];
  if (
    formData.livingSituationAtAddress.toLowerCase() !== "buyer" &&
    formData.ownsInvestmentProperty === "Yes" &&
    formData.investmentPropertyMortgaged === "Yes" &&
    !declaredMortgage(formData)
  ) {
    statuses.push({
      kind: "error",
      message:
        "You must declare one mortgage as you have told us your investment property is mortgaged.",
    });
  }

  if (
    formData.loanPurpose.toLowerCase() === "consolidation" &&
    checkForLoanOrCards(formData).filter((debt) => debt.consolidate.length > 0)
      .length === 0
  ) {
    statuses.push({
      kind: "error",
      message:
        "Please ensure that at least one credit card or personal loan is being consolidated",
    });
  }

  if (
    formData.loanPurpose.toLowerCase() !== "consolidation" &&
    formData.hasDebts.toLowerCase() === "yes" &&
    !hasAddedDebts(formData)
  ) {
    statuses.push({
      kind: "error",
      message: "Please add all your debts",
    });
  }

  if (statuses.length > 0) {
    return statuses;
  }

  return undefined;
};

const declaredMortgage = (formData: Application): boolean => {
  let mortgages = 0;
  for (let i = 1; i <= 10; i++) {
    const key =
      `financialDebtType_${i}` as keyof typeof applicationFormDefaults;
    if (formData[key] === MORTGAGE_VALUE) {
      mortgages += 1;
    }
  }
  return mortgages > 0;
};

//analytics helpers
export type ApplicationAnalytics = {
  eventName: string;
  loanPurpose: string;
  status: string;
  loanId: string;
  statementPreference?: "digital" | "manual";
  equifax?: number;
  equifaxOneScore?: number;
  lapSource?: string;
};

export function sendLoanApplicationAmplitudeEvent({
  eventName,
  loanPurpose,
  status,
  loanId,
  statementPreference,
  equifaxOneScore,
  lapSource,
}: ApplicationAnalytics) {
  const loanType = loanPurpose === "Car" ? "Secured" : "Unsecured";
  const startApplicationPageVersion = "v2";
  quoteAmplitudeHelper.sendEvent(
    eventName,
    {
      "Loan id": loanId,
      "Loan application status": status,
      "Loan purpose": loanPurpose,
      "Loan type": loanType,
      "Bank statement preference": statementPreference
        ? capitalise(statementPreference)
        : null,
      "LAP source": lapSource,
      [ABTestProperties.START_APP_PAGE]: startApplicationPageVersion,
    },
    {
      "Loan application status": status,
      "Loan purpose": loanPurpose,
      "Loan type": loanType,
      "AS Equifax One Score": equifaxOneScore,
      "LAP source": lapSource,
      [ABTestProperties.START_APP_PAGE]: startApplicationPageVersion,
    }
  );
}

export const getApplicationFromActive = (
  activeApplications: IncompleteLoanApplication[],
  loanId: string | null | (string | null)[]
) => {
  if (typeof loanId === "string") {
    return activeApplications.find(
      (activeApplication) => `${activeApplication.loanId}` === loanId
    );
  }

  return null;
};
